import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, Heart, ChatCircle, UserPlus, Airplane, Sparkle, ArrowBendDoubleUpLeft, Flag, BellRinging, BellSlash, Check, X } from "@phosphor-icons/react";
import { notificationsApi, notificationsStreamUrl, friendsApi } from "@/lib/apiClient";
import { pushSupported, currentPushStatus, enablePush, disablePush } from "@/lib/push";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { dispatchFriendStatusChange } from "./FriendButton";

const ICONS = {
    reaction: Heart,
    comment: ChatCircle,
    reply: ArrowBendDoubleUpLeft,
    comment_like: Heart,
    follow: UserPlus,
    mention: Sparkle,
    mention_comment: Sparkle,
    tag: Airplane,
    warning: Flag,
    friend_request: UserPlus,
    friend_accept: Sparkle,
};

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const s = Math.round((Date.now() - d.getTime()) / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.round(h / 24)}d`;
}

function describe(n, t) {
    const actor = n.actor_name || (n.actor_handle ? `@${n.actor_handle}` : t("notificationsBell.descriptions.someone"));
    switch (n.kind) {
        case "reaction": return t("notificationsBell.descriptions.reaction", { actor });
        case "comment": return t("notificationsBell.descriptions.comment", { actor });
        case "reply": return t("notificationsBell.descriptions.reply", { actor });
        case "comment_like": return t("notificationsBell.descriptions.commentLike", { actor });
        case "follow": return t("notificationsBell.descriptions.follow", { actor });
        case "mention": return t("notificationsBell.descriptions.mention", { actor });
        case "mention_comment": return t("notificationsBell.descriptions.mentionComment", { actor });
        case "tag": return t("notificationsBell.descriptions.tag", { actor });
        case "warning": return t("notificationsBell.descriptions.warning", { note: n.note || t("notificationsBell.descriptions.defaultNote") });
        case "friend_request": return n.message || `${actor} sent you a friend request.`;
        case "friend_accept": return n.message || `${actor} accepted your friend request.`;
        default: return t("notificationsBell.descriptions.fallback", { actor, kind: n.kind });
    }
}

export default function NotificationsBell() {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const ref = useRef(null);

    const load = async () => {
        try {
            const r = await notificationsApi.list();
            setItems(r.items || []);
            setUnread(r.unread || 0);
        } catch {}
    };

    useEffect(() => {
        load();
        const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onClick);

        // Live SSE — falls back to 30s polling on error
        const url = notificationsStreamUrl();
        let es = null;
        let pollTimer = null;
        const startPolling = () => {
            if (pollTimer) return;
            pollTimer = setInterval(load, 30000);
        };
        const stopPolling = () => {
            if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
        };
        if (url && typeof window !== "undefined" && "EventSource" in window) {
            try {
                es = new EventSource(url, { withCredentials: true });
                es.addEventListener("unread", (ev) => {
                    try {
                        const d = JSON.parse(ev.data);
                        setUnread(d.unread || 0);
                    } catch {}
                });
                es.addEventListener("notification", (ev) => {
                    try {
                        const n = JSON.parse(ev.data);
                        setItems((prev) => [{ ...n, read: false }, ...prev].slice(0, 60));
                        setUnread((u) => u + 1);
                    } catch {}
                });
                es.onerror = () => {
                    // Connection dropped — fall back to polling
                    if (es) { es.close(); es = null; }
                    startPolling();
                };
            } catch {
                startPolling();
            }
        } else {
            startPolling();
        }

        return () => {
            document.removeEventListener("mousedown", onClick);
            if (es) es.close();
            stopPolling();
        };
    }, []);

    const openPanel = async () => {
        setOpen((v) => !v);
        if (!open && unread > 0) {
            try {
                await notificationsApi.readAll();
                setUnread(0);
                setItems((prev) => prev.map((n) => ({ ...n, read: true })));
            } catch {}
        }
    };

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={openPanel}
                className="w-10 h-10 rounded-full glass grid place-items-center relative"
                aria-label={t("notificationsBell.ariaLabel")}
                data-testid="notifications-bell"
            >
                <Bell size={14} weight={unread > 0 ? "fill" : "regular"} />
                {unread > 0 && (
                    <span className="absolute -top-1 -right-1 text-[10px] font-bold min-w-[18px] h-[18px] rounded-full grid place-items-center px-1" style={{ background: "var(--gold)", color: "var(--navy)" }}>
                        {unread > 99 ? "99+" : unread}
                    </span>
                )}
            </button>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -6, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.98 }}
                        className="absolute right-0 top-12 z-50 w-[340px] max-w-[92vw] rounded-2xl overflow-hidden"
                        style={{ background: "var(--navy)", border: "1px solid rgba(212,175,55,0.25)", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}
                        data-testid="notifications-panel"
                    >
                        <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                            <div className="overline">{t("notificationsBell.title")}</div>
                            <Link to="/settings" className="text-[10px] text-white/50 hover:text-white">{t("notificationsBell.preferences")}</Link>
                        </div>
                        <div className="max-h-[420px] overflow-y-auto divide-y" style={{ borderColor: "rgba(255,255,255,0.04)" }}>
                            {items.length === 0 ? (
                                <div className="p-8 text-center text-white/50 text-sm">{t("notificationsBell.caughtUp")}</div>
                            ) : items.map((n) => {
                                const Icon = ICONS[n.kind] || Bell;
                                const inner = (
                                    <div className="p-3 flex gap-3 items-start hover:bg-white/[0.03]" style={{ background: n.read ? "transparent" : "rgba(212,175,55,0.05)" }} data-testid={`notif-${n.id}`}>
                                        <img src={n.actor_avatar || `https://api.dicebear.com/7.x/notionists/svg?seed=${n.actor_handle || "x"}`} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-white/85 leading-snug">{describe(n, t)}</div>
                                            {n.preview && <div className="text-xs text-white/45 mt-0.5 line-clamp-1">"{n.preview}"</div>}
                                            <div className="text-[10px] text-white/40 mt-1 flex items-center gap-1.5">
                                                <Icon size={10} color="#D4AF37" />
                                                <span>{timeAgo(n.created_at)}</span>
                                            </div>
                                            {n.kind === "friend_request" && n.actor_handle && (
                                                <FriendRequestActions
                                                    handle={n.actor_handle}
                                                    onDone={() => {
                                                        setItems((prev) => prev.filter((x) => x.id !== n.id));
                                                        setUnread((u) => Math.max(0, u - (n.read ? 0 : 1)));
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                );
                                if (n.post_id) return <Link key={n.id} to={`/app`} onClick={() => setOpen(false)}>{inner}</Link>;
                                return <div key={n.id}>{inner}</div>;
                            })}
                        </div>
                        <PushToggle />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function PushToggle() {
    const { t } = useTranslation();
    const [status, setStatus] = useState({ supported: false, permission: "default", subscribed: false });
    const [busy, setBusy] = useState(false);
    const supported = pushSupported();

    useEffect(() => {
        if (!supported) return;
        currentPushStatus().then(setStatus);
    }, [supported]);

    if (!supported) return null;

    const on = async () => {
        setBusy(true);
        try {
            await enablePush();
            setStatus(await currentPushStatus());
            toast.success(t("notificationsBell.toasts.pushEnabled"));
        } catch (e) {
            const m = String(e?.message || e || "");
            if (m.includes("permission-denied")) toast.error(t("notificationsBell.toasts.blockedError"));
            else toast.error(t("notificationsBell.toasts.pushEnableFailed"));
        } finally { setBusy(false); }
    };

    const off = async () => {
        setBusy(true);
        try {
            await disablePush();
            setStatus(await currentPushStatus());
            toast.success(t("notificationsBell.toasts.pushDisabled"));
        } catch { toast.error(t("notificationsBell.toasts.pushDisableFailed")); }
        finally { setBusy(false); }
    };

    return (
        <div className="p-3 border-t flex items-center justify-between gap-3" style={{ borderColor: "rgba(255,255,255,0.06)" }} data-testid="push-toggle">
            <div className="flex items-center gap-2 min-w-0">
                {status.subscribed ? <BellRinging size={14} weight="fill" color="#D4AF37" /> : <BellSlash size={14} color="rgba(255,255,255,0.5)" />}
                <div className="min-w-0">
                    <div className="text-[12px] text-white/85 leading-tight">{status.subscribed ? t("notificationsBell.webPush.on") : t("notificationsBell.webPush.off")}</div>
                    <div className="text-[10px] text-white/40 leading-tight line-clamp-1">
                        {status.permission === "denied" ? t("notificationsBell.webPush.blocked") : status.subscribed ? t("notificationsBell.webPush.subscribedSub") : t("notificationsBell.webPush.unsubscribedSub")}
                    </div>
                </div>
            </div>
            {status.subscribed ? (
                <button onClick={off} disabled={busy} className="text-[11px] tracking-[0.15em] uppercase text-white/60 hover:text-white disabled:opacity-50" data-testid="push-toggle-off">
                    {t("notificationsBell.webPush.turnOff")}
                </button>
            ) : (
                <button onClick={on} disabled={busy || status.permission === "denied"} className="text-[11px] tracking-[0.15em] uppercase disabled:opacity-50" style={{ color: "var(--gold)" }} data-testid="push-toggle-on">
                    {busy ? t("notificationsBell.webPush.enabling") : t("notificationsBell.webPush.turnOn")}
                </button>
            )}
        </div>
    );
}

// Inline Accept / Decline buttons rendered inside a friend_request notification row.
// onDone() is called after either action so the parent can remove the notification from the list.
function FriendRequestActions({ handle, onDone }) {
    const [busy, setBusy] = useState(false);

    const accept = async (e) => {
        e.stopPropagation();
        if (busy) return;
        setBusy(true);
        try {
            await friendsApi.accept(handle);
            dispatchFriendStatusChange(handle);
            toast.success("Friend request accepted.");
            onDone();
        } catch {
            toast.error("An error occurred.");
        } finally {
            setBusy(false);
        }
    };

    const decline = async (e) => {
        e.stopPropagation();
        if (busy) return;
        setBusy(true);
        try {
            await friendsApi.reject(handle);
            dispatchFriendStatusChange(handle);
            toast.success("Friend request declined.");
            onDone();
        } catch {
            toast.error("An error occurred.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex gap-2 mt-2">
            <button
                onClick={accept}
                disabled={busy}
                className="inline-flex items-center gap-1 rounded-full text-[11px] font-medium px-3 py-1 transition-colors disabled:opacity-50"
                style={{ background: "#10b981", color: "white" }}
                data-testid={`notif-accept-${handle}`}
            >
                <Check size={11} weight="bold" /> Accept
            </button>
            <button
                onClick={decline}
                disabled={busy}
                className="inline-flex items-center gap-1 rounded-full text-[11px] font-medium px-3 py-1 transition-colors disabled:opacity-50"
                style={{ border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.6)" }}
                data-testid={`notif-decline-${handle}`}
            >
                <X size={11} weight="bold" /> Decline
            </button>
        </div>
    );
}

